import styled, { css, keyframes } from 'styled-components'
import { stoColor, stoMedia, stoSpace, stoType } from 'constants/stoTheme'

/** Figma 4582:136814: rows 16px apart, each followed by a 1px rule, the last one too. */
export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
`

export const Rule = styled.div`
  height: 1px;
  background: ${stoColor.borderSubtle};
`

/** The list, then the button 24px under it (Figma 4582:136811). */
export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[6]};
`

/** Text on the left, date on the right, 8px apart; below tablet the date moves above the title. */
export const Row = styled.article`
  display: flex;
  align-items: flex-start;
  gap: ${stoSpace[2]};

  ${stoMedia.belowTablet} {
    flex-direction: column-reverse;
  }
`

export const RowText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  color: ${stoColor.textPrimary};
  overflow-wrap: anywhere;
`

export const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${stoSpace[1]};
`

/** A <p>: the global heading rules would resize an <h3>. */
export const Title = styled.p`
  ${stoType.bodyMdCallout};
  margin: 0;
`

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 80, 202, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(0, 80, 202, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 80, 202, 0); }
`

/** Figma Ellipse 64: 8px, action/primary blue. Unseen ones pulse until the next visit. */
export const Dot = styled.span<{ $blink: boolean }>`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${stoColor.primary};

  ${({ $blink }) =>
    $blink &&
    css`
      animation: ${pulse} 1.6s ease-out infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`

/**
 * Back-office rich text at body/md, whatever the editor pasted: sizes, fonts and colours from it
 * (inline styles included) give way to the card's, so every update reads the same.
 */
export const Text = styled.div<{ $clamped: boolean; $fade: boolean }>`
  ${stoType.bodyMd};

  /* A long update shows TEXT_LINES lines (20px each) until "Show more". */
  ${({ $clamped }) =>
    $clamped &&
    css`
      max-height: calc(var(--update-lines) * 20px);
      overflow: hidden;
    `}
  ${({ $fade }) =>
    $fade &&
    css`
      mask-image: linear-gradient(to bottom, black 50%, transparent);
    `}

  * {
    font-family: inherit !important;
    font-size: inherit !important;
    line-height: inherit !important;
    letter-spacing: inherit !important;
    color: inherit !important;
    background: none !important;
  }

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: inherit;
  }

  strong,
  b {
    font-weight: 600;
  }

  ul,
  ol {
    margin: 0;
    padding-left: ${stoSpace[5]};
  }

  a {
    text-decoration: underline;
  }
`

/** Show more / Open PDF: one line of actions under the text. */
export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${stoSpace[4]};
  margin-top: ${stoSpace[2]};
`

export const MoreToggle = styled.button`
  ${stoType.bodyMdCallout};
  padding: 0;
  border: none;
  background: none;
  color: ${stoColor.textPrimary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const LinkOut = styled.a`
  ${stoType.bodyMdCallout};
  align-self: flex-start;
  color: ${stoColor.textPrimary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const UpdateDate = styled.span`
  ${stoType.bodyMd};
  flex: 0 0 auto;
  white-space: nowrap;
  color: ${stoColor.textSecondary};
`

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`
